import React from "react";

export const INTERNAL_FORM_AUTOCOMPLETE = "off";

// Chrome behandelt CRM-Personenfelder sonst als private Browser-Adressdaten.
export const NO_BROWSER_AUTOFILL = "new-password";

export const NO_BROWSER_AUTOFILL_INPUT_PROPS = {
  autoComplete: NO_BROWSER_AUTOFILL,
  "data-1p-ignore": "true",
  "data-form-type": "other",
  "data-lpignore": "true",
  "data-mqorva-no-browser-autofill": "true"
} as const;

export const NO_BROWSER_EMAIL_AUTOFILL_INPUT_PROPS = {
  ...NO_BROWSER_AUTOFILL_INPUT_PROPS,
  inputMode: "email",
  type: "text"
} as const;

export function noBrowserAutofillFieldInputProps(fieldId: string) {
  return {
    ...NO_BROWSER_AUTOFILL_INPUT_PROPS,
    id: `mq-${fieldId}`,
    name: `mq-${fieldId}`
  } as const;
}

export const noBrowserAddressAutofillInputProps = noBrowserAutofillFieldInputProps;

const ADDRESS_AUTOFILL_SUFFIXES = { street: "a", zip: "b", city: "c", country: "d" } as const;
const PERSON_AUTOFILL_SUFFIXES = {
  title: "t",
  firstName: "f",
  lastName: "l",
  email: "e",
  phone: "p",
  mobile: "m",
  street: "a",
  zip: "b",
  city: "c",
  country: "d",
  birthPlace: "g",
  nationality: "n",
  contactName: "k"
} as const;
const BANK_AUTOFILL_SUFFIXES = {
  accountHolder: "a",
  iban: "b",
  bic: "c",
  bankName: "d",
  mandateReference: "e"
} as const;

function opaqueAutofillFieldMaps<T extends string>(prefix: string, suffixes: { readonly [K in T]: string }) {
  const inputProps = {} as Record<T, ReturnType<typeof noBrowserAutofillFieldInputProps>>;
  const labelHtmlFor = {} as Record<T, string>;
  for (const key of Object.keys(suffixes) as T[]) {
    inputProps[key] = noBrowserAutofillFieldInputProps(`${prefix}-${suffixes[key]}`);
    labelHtmlFor[key] = `mq-${prefix}-label-${suffixes[key]}`;
  }
  return { inputProps, labelHtmlFor };
}

export function emailBrowserAutofillInputProps(
  fieldProps: ReturnType<typeof noBrowserAutofillFieldInputProps>
) {
  return {
    ...NO_BROWSER_EMAIL_AUTOFILL_INPUT_PROPS,
    ...fieldProps
  } as const;
}

/**
 * Adressfelder mit undurchsichtigen id/name-Werten versehen, damit Chrome sie nicht als
 * private Anschrift erkennt und nach dem Speichern das Angebot „Adresse speichern?" zeigt.
 * `autocomplete` allein genügt dafür nicht — Chrome klassifiziert über Feldnamen und Label.
 * Das `htmlFor` der Labels zeigt deshalb bewusst auf eine andere Kennung als das Eingabefeld.
 */
export function useAddressAutofillFieldProps(prefix?: string) {
  const generatedPrefix = React.useId().replace(/[^a-zA-Z0-9]/g, "");
  const effectivePrefix = prefix ?? generatedPrefix;
  return React.useMemo(
    () => opaqueAutofillFieldMaps(effectivePrefix, ADDRESS_AUTOFILL_SUFFIXES),
    [effectivePrefix]
  );
}

/**
 * Personen-Stammdaten inkl. Anschrift: Chrome klassifiziert Vorname/Nachname/E-Mail
 * zusammen mit Straße/PLZ/Ort als private Adresse und bietet nach dem Speichern
 * „Adresse speichern?“ an — auch wenn die Adressfelder schon undurchsichtig sind.
 */
export function usePersonAutofillFieldProps(prefix?: string) {
  const generatedPrefix = React.useId().replace(/[^a-zA-Z0-9]/g, "");
  const effectivePrefix = prefix ?? generatedPrefix;
  return React.useMemo(
    () => opaqueAutofillFieldMaps(effectivePrefix, PERSON_AUTOFILL_SUFFIXES),
    [effectivePrefix]
  );
}

/**
 * Bankfelder mit undurchsichtigen id/name-Werten versehen, damit Chrome sie nicht als
 * Zahlungs- oder Adressdaten erkennt und Autofill-Vorschläge einblendet.
 * `autocomplete` allein reicht nicht — Chrome klassifiziert über Feldnamen wie IBAN und Label.
 */
export function useBankAutofillFieldProps(prefix?: string) {
  const generatedPrefix = React.useId().replace(/[^a-zA-Z0-9]/g, "");
  const effectivePrefix = prefix ?? generatedPrefix;
  return React.useMemo(
    () => opaqueAutofillFieldMaps(effectivePrefix, BANK_AUTOFILL_SUFFIXES),
    [effectivePrefix]
  );
}
